import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { PurchasePartiesProps } from "../../types/purchaseParties";

export interface PurchasePartiesState {
  purchaseParties: PurchasePartiesProps[];
  totalParties: number;
  totalPendingAmount: number;
  totalReceivedAmount: number;
  isPartyModalOpen: boolean;
  editParty: PurchasePartiesProps | null;
  loading: boolean;
  error: string | null;
}

const initialState: PurchasePartiesState = {
  purchaseParties: [],
  totalParties: 0,
  totalPendingAmount: 0,
  totalReceivedAmount: 0,
  isPartyModalOpen: false,
  editParty: null,
  loading: false,
  error: null,
};

type FetchPurchasePartiesListDataProps = {
  page: number;
  size?: number;
  fromDate?: string | null;
  toDate?: string | null;
  search?: string | null;
};

export const fetchPurchasePartiesListData = createAsyncThunk(
  "fetchPurchasePartiesListData",
  async (req: FetchPurchasePartiesListDataProps) => {
    let response: any;
    await GET(`/v1/admin/purchase-parties`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const purchasePartiesSlice = createSlice({
  name: "purchaseParties",
  initialState,
  reducers: {
    togglePurchasePartyModal: (state, action: PayloadAction<boolean>) => {
      state.isPartyModalOpen = action.payload;
    },
    setEditPurchaseParty: (
      state,
      action: PayloadAction<PurchasePartiesProps | null>
    ) => {
      state.editParty = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchPurchasePartiesListData.pending,
      (state, action: any) => {
        state.purchaseParties = [];
        state.loading = true;
        state.error = null;
      }
    );
    builder.addCase(
      fetchPurchasePartiesListData.fulfilled,
      (state, action: any) => {
        state.loading = false;
        if (action.payload.data.status === "success") {
          state.purchaseParties = action.payload.data.data.records;
          state.totalParties = action.payload.data.data.total_records;
          state.totalPendingAmount =
            action.payload.data.data.total_pending_amount;
          state.totalReceivedAmount =
            action.payload.data.data.total_received_amount;
          state.error = null;
        } else {
          state.purchaseParties = [];
          state.totalPendingAmount = 0;
          state.totalReceivedAmount = 0;
          state.error =
            action?.payload?.data?.message ||
            "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
        }
      }
    );
    builder.addCase(
      fetchPurchasePartiesListData.rejected,
      (state, action: any) => {
        state.purchaseParties = [];
        state.loading = false;
        state.error =
          action?.payload?.data?.message ||
          "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";
      }
    );
  },
});

export const { togglePurchasePartyModal, setEditPurchaseParty } =
  purchasePartiesSlice.actions;

export const getPurchaseParties = (state: RootState) =>
  state.purchaseParties.purchaseParties;

export const getTotalPurchaseParties = (state: RootState) =>
  state.purchaseParties.totalParties;

export const getTotalPurchasePendingAmount = (state: RootState) =>
  state.purchaseParties.totalPendingAmount;

export const getTotalPurchaseReceivedAmount = (state: RootState) =>
  state.purchaseParties.totalReceivedAmount;

export const getIsPurchasePartyModalOpen = (state: RootState) =>
  state.purchaseParties.isPartyModalOpen;

export const getEditPurchaseParty = (state: RootState) =>
  state.purchaseParties.editParty;

export default purchasePartiesSlice.reducer;
